<template>
  <div id="app-container">
    <div class="main-layout" v-if="isAuthenticated">
      <aside class="sidebar-gradient">
        <div class="logo-area">
          <i class="fas fa-cloud logo-icon"></i>
          <span class="logo-title">{{ currentProvider === 'aws' ? t('navigation.awsManager') : t('navigation.gcpManager') }}</span>
        </div>

        <!-- GCP项目选择器 -->
        <div v-if="currentProvider === 'gcp'" class="project-selector">
          <label>{{ t('navigation.projectId') }}:</label>
          <select v-if="projects && projects.length > 1" v-model="selectedProject" @change="onProjectChange">
            <option v-for="p in projects" :key="p" :value="p">{{ p }}</option>
          </select>
          <span v-else-if="projects && projects.length === 1">{{ projects[0] }}</span>
          <span v-else>{{ t('common.loading') }}</span>
        </div>

        <!-- AWS模式：固定区域ap-northeast-1 -->
        <div v-else-if="currentProvider === 'aws'" class="region-info">
          <span class="fixed-region">ap-northeast-1 (Asia Pacific - Tokyo)</span>
        </div>

        <div v-if="!resources.vms.length && !loading" class="load-prompt">
          <i class="fas fa-info-circle"></i>
          <span>{{ t('navigation.loadPrompt') }}</span>
        </div>

        <button class="refresh-btn" @click="loadResourcesHandler">
          <i class="fas fa-sync-alt"></i> {{ t('navigation.loadResources') }}
        </button>

        <div class="stats-cards">
          <div class="stat-card">
            <i class="fas fa-server"></i>
            <div class="stat-num">{{ resources.vms?.length || 0 }}</div>
            <div class="stat-label">{{ t('stats.virtualMachines') }}</div>
          </div>
          <div class="stat-card">
            <i class="fas fa-hdd"></i>
            <div class="stat-num">{{ resources.disks?.length || 0 }}</div>
            <div class="stat-label">{{ t('stats.disks') }}</div>
          </div>
        </div>

        <button @click="showCreateVMModal = true" class="main-btn green" v-if="user && user.role !== 'viewer'">
          <i class="fas fa-plus"></i> {{ t('stats.createVM') }}
        </button>
        <button @click="showCreateDiskModal = true" class="main-btn blue" v-if="user && user.role !== 'viewer'">
          <i class="fas fa-hdd"></i> {{ t('stats.createDisk') }}
        </button>

        <nav class="navigation">
          <button @click="currentView = 'topology'" :class="{ active: currentView === 'topology' }" class="nav-btn">
            <i class="fas fa-sitemap"></i> {{ t('navigation.topology') }}
          </button>
          <button @click="currentView = 'list'" :class="{ active: currentView === 'list' }" class="nav-btn">
            <i class="fas fa-list"></i> {{ t('navigation.list') }}
          </button>
          <button @click="currentView = 'audit'" :class="{ active: currentView === 'audit' }" class="nav-btn" v-if="user && user.role === 'admin'">
            <i class="fas fa-history"></i> {{ t('navigation.audit') }}
          </button>
        </nav>

        <div class="filters">
          <input type="search" v-model="searchQuery" :placeholder="t('filters.searchPlaceholder')" class="search-input" />
          <div class="checkbox-container">
            <input type="checkbox" id="show-running-only" v-model="showRunningOnly" />
            <label for="show-running-only">{{ t('filters.showRunningOnly') }}</label>
          </div>
        </div>


        <!-- 用户信息区 -->
        <div class="sidebar-userinfo" v-if="user">
          <div class="userinfo-email">{{ user.email }}</div>
          <div class="userinfo-role">({{ user.role }})
          </div>
        </div>
      </aside>

      <main class="content-area">
        <!-- 语言切换器 - 右上角悬浮 -->
        <div class="floating-language-switcher">
          <LanguageSwitcher />
        </div>
        
        <!-- 站点切换器 - 右上角悬浮 -->
        <div class="floating-site-switcher">
          <SiteSwitcher />
        </div>

        <TopologyView
          v-if="currentView === 'topology' && filteredResources && user"
          :resources="filteredResources"
          :user="user"
          :currentProjectId="currentProjectId"
          :isOperating="isOperating"
          @resource-click="onResourceClick"
          @attach-disk="onAttachDisk"
          @vm-operation="onVmOperation"
          @detach-disk="onDetachDisk"
          @delete-disk="onDeleteDisk"
        />
        <ListView
          v-if="currentView === 'list' && filteredResources && user"
          :resources="filteredResources"
          :user="user"
          :currentProjectId="currentProjectId"
          :isOperating="isOperating"
          @resource-click="onResourceClick"
          @attach-disk="onAttachDisk"
          @vm-operation="onVmOperation"
          @detach-disk="onDetachDisk"
          @delete-disk="onDeleteDisk"
          @labels-updated="onLabelsUpdated"
        />
        <AuditLogView
          v-if="currentView === 'audit' && user"
        />
      </main>
    </div>

    <div class="loading-overlay" v-if="loading || isAuthLoading">
      <div class="spinner"></div>
    </div>

    <!-- Modals -->
    <CreateVMModal
      v-if="showCreateVMModal"
      @close="showCreateVMModal = false"
      @created="onVMCreated"
    />
    <CreateDiskModal
      v-if="showCreateDiskModal"
      @close="showCreateDiskModal = false"
      @created="onDiskCreated"
    />
    

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TopologyView from './components/TopologyView.vue';
import ListView from './components/ListView.vue';
import CreateVMModal from './components/CreateVMModal.vue';
import CreateDiskModal from './components/CreateDiskModal.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import SiteSwitcher from './components/SiteSwitcher.vue';

import AuditLogView from './components/AuditLogView.vue';
import { useResources } from './composables/useResources.js';
import { useAuth } from './composables/useAuth.js';
import { useCloudProvider } from './composables/useCloudProvider.js';
import { UserManager } from 'oidc-client-ts';

const { t } = useI18n();

const currentView = ref('topology');
const searchQuery = ref('');
const showRunningOnly = ref(false);
const showCreateVMModal = ref(false);
const showCreateDiskModal = ref(false);
const isOperating = ref(false);
const cognitoUser = ref(null);

// Cognito OIDC configuration (从服务器动态获取)
const cognitoAuthConfig = ref(null);
const userManager = ref(null);

// 初始化 Cognito 配置
const initializeCognitoConfig = async () => {
  try {
    const config = await getFullConfig();
    if (config.cognitoAuth) {
      cognitoAuthConfig.value = {
        authority: config.cognitoAuth.authority,
        client_id: config.cognitoAuth.clientId,
        redirect_uri: config.cognitoAuth.redirectUri,
        response_type: config.cognitoAuth.responseType,
        scope: config.cognitoAuth.scope
      };

      // 创建 UserManager 实例
      userManager.value = new UserManager(cognitoAuthConfig.value);

      // 将 userManager 暴露到全局，供 useAuth 使用
      window.userManager = userManager.value;

      console.log('Cognito 配置已从服务器加载:', cognitoAuthConfig.value);
    } else {
      console.log('当前不是 AWS 模式，跳过 Cognito 配置');
    }
  } catch (error) {
    console.error('加载 Cognito 配置失败:', error);
  }
};

// 定期检查token状态的函数
const checkTokenStatus = async () => {
  if (currentProvider.value === 'aws' && userManager.value && isAuthenticated.value) {
    try {
      const cognitoUser = await userManager.value.getUser();
      if (cognitoUser && cognitoUser.access_token) {
        // 检查token是否即将过期（提前10分钟）
        const payload = JSON.parse(atob(cognitoUser.access_token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const expirationTime = payload.exp;
        
        if (expirationTime && (expirationTime - currentTime) < 600) { // 10分钟内过期
          console.log('Token即将过期，尝试静默刷新...');
          try {
            await userManager.value.signinSilent();
            console.log('Token静默刷新成功');
          } catch (error) {
            console.error('Token静默刷新失败:', error);
          }
        }
      }
    } catch (error) {
      console.error('检查token状态失败:', error);
    }
  }
};

// Event listeners for user changes
const userLoaded = (loadedUser) => {
  cognitoUser.value = loadedUser;
  isAuthenticated.value = !!loadedUser;
  console.log('Cognito user loaded:', loadedUser);
  
  // 如果用户已加载，更新session storage中的用户信息
  if (loadedUser) {
    const userInfo = {
      email: loadedUser.profile?.email || loadedUser.profile?.sub || 'unknown',
      role: loadedUser.profile?.['cognito:groups']?.includes('admin') ? 'admin' :
        loadedUser.profile?.['cognito:groups']?.includes('editor') ? 'editor' : 'viewer',
      id: loadedUser.profile?.sub || loadedUser.profile?.id || 'unknown'
    };
    
    // 更新用户信息
    user.value = userInfo;
    
    // 更新session storage
    sessionStorage.setItem('cognito_authenticated', 'true');
    sessionStorage.setItem('cognito_user', JSON.stringify(userInfo));
    
    console.log('用户信息已更新到session storage:', userInfo);
  }
};

const userUnloaded = () => {
  user.value = null;
  isAuthenticated.value = false;
};

// 设置事件监听器
const setupEventListeners = () => {
  if (userManager.value) {
    userManager.value.events.addUserLoaded(userLoaded);
    userManager.value.events.addUserUnloaded(userUnloaded);
    
    // 添加token即将过期的事件监听器
    userManager.value.events.addAccessTokenExpiring(() => {
      console.log('Token即将过期，准备刷新...');
    });
    
    // 添加token过期的事件监听器
    userManager.value.events.addAccessTokenExpired(() => {
      console.log('Token已过期，尝试静默刷新...');
      // 尝试静默刷新token
      userManager.value.signinSilent().catch(error => {
        console.error('静默刷新失败:', error);
        // 如果静默刷新失败，可能需要重新登录
        console.log('静默刷新失败，准备重新登录...');
        userManager.value.signinRedirect();
      });
    });
    
    // 添加用户会话移除的事件监听器
    userManager.value.events.addUserSessionChanged(() => {
      console.log('用户会话发生变化');
      // 清除session storage中的认证状态
      sessionStorage.removeItem('cognito_authenticated');
      sessionStorage.removeItem('cognito_user');
    });
  }
};
// 云服务提供者相关
const { currentProvider, configLoaded, getFullConfig } = useCloudProvider();

const {
  projects,
  selectedProject,
  resources,
  loading,
  currentProjectId,
  fetchProjects,
  selectProject,
  operateVM,
  deleteDisk,
  attachDiskToVM,
  detachDiskFromVM
} = useResources();

const {
  user,
  setUser,
  isAuthenticated,
  loading: isAuthLoading,
  initializeAuth
} = useAuth();


// eslint-disable-next-line no-undef
const autoLoadOnStart = VITE_AUTO_LOAD_ON_START === 'true';

onMounted(async () => {
  try {
    console.log('开始初始化应用...');

    // 初始化 Cognito 配置
    await initializeCognitoConfig();

    // 设置事件监听器
    setupEventListeners();

    // 检查 sessionStorage 中的认证状态
    const isCognitoAuthenticated = sessionStorage.getItem('cognito_authenticated');
    const cognitoUserData = sessionStorage.getItem('cognito_user');

    if (isCognitoAuthenticated === 'true' && cognitoUserData) {
      console.log('从 sessionStorage 恢复认证状态...');
      try {
        const userData = JSON.parse(cognitoUserData);
        console.log('解析的用户数据:', userData);

        // 设置用户信息
        setUser(userData);

        // 等待下一个 tick 确保响应式更新完成
        await new Promise(resolve => setTimeout(resolve, 0));

        console.log('认证状态恢复成功:', userData);
        console.log('恢复后的 isAuthenticated:', isAuthenticated.value);
        console.log('恢复后的 user.value:', user.value);

        // 如果认证状态恢复成功，直接返回，不继续执行后续逻辑
        if (isAuthenticated.value) {
          console.log('认证状态恢复成功，跳过后续认证检查');
        }
      } catch (error) {
        console.error('恢复认证状态失败:', error);
        sessionStorage.removeItem('cognito_authenticated');
        sessionStorage.removeItem('cognito_user');
      }
    } else {
      console.log('没有找到 sessionStorage 中的认证状态');
      console.log('isCognitoAuthenticated:', isCognitoAuthenticated);
      console.log('cognitoUserData:', cognitoUserData);
    }

    // 检查是否是 Cognito 回调页面
    const url = new URL(window.location.href);
    if (url.pathname === '/auth/cognito/callback') {
      console.log('检测到 Cognito 回调页面，处理回调...');
      if (url.searchParams.has('code')) {
        try {
          console.log('处理 Cognito 回调，授权码:', url.searchParams.get('code'));
          const userResponse = await userManager.value.signinCallback();
          await userManager.value.clearStaleState();

          // 设置用户信息，isAuthenticated 会自动更新
          cognitoUser.value = userResponse;
          // 从 userResponse 中提取用户信息
          const userInfo = {
            email: userResponse.profile?.email || userResponse.profile?.sub || 'unknown',
            role: userResponse.profile?.['cognito:groups']?.includes('admin') ? 'admin' :
              userResponse.profile?.['cognito:groups']?.includes('editor') ? 'editor' : 'viewer',
            id: userResponse.profile?.sub || userResponse.profile?.id || 'unknown'
          };

          user.value = userInfo; // 这会自动更新 isAuthenticated

          // 保存认证状态到 sessionStorage，防止页面重定向后丢失
          sessionStorage.setItem('cognito_authenticated', 'true');
          sessionStorage.setItem('cognito_user', JSON.stringify(userInfo));

          console.log('userResponse', userResponse);
          console.log('认证成功，重定向到首页...');

          // 使用 window.location.href 确保完全重定向
          window.location.href = '/';
          return;
        } catch (error) {
          console.error('Cognito 回调处理失败:', error);
          window.location.href = '/?error=callback_error';
          return;
        }
      } else if (url.searchParams.has('error')) {
        console.error('Cognito 授权错误:', url.searchParams.get('error'));
        window.location.href = `/?error=${url.searchParams.get('error')}`;
        return;
      }
    }

    console.log('等待云服务配置加载...');
    while (!configLoaded.value) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    console.log('云服务配置加载完成，当前提供者:', currentProvider.value);

    if (currentProvider.value === 'gcp') {
      await initializeAuth();
    }
    console.log('认证初始化完成，认证状态:', isAuthenticated.value);
    console.log('当前用户信息:', user.value);
    console.log('当前云服务提供者:', currentProvider.value);

    // 如果是AWS模式且已认证，启动定期token检查
    if (currentProvider.value === 'aws' && isAuthenticated.value) {
      console.log('启动定期token状态检查...');
      // 每5分钟检查一次token状态
      setInterval(checkTokenStatus, 5 * 60 * 1000);
    }

    if (isAuthenticated.value) {
      // 等待云服务配置加载完成
      if (currentProvider.value === 'aws') {
        // AWS模式：直接加载AWS资源
        console.log('AWS模式：准备加载AWS资源...');
        currentProjectId.value = 'ap-northeast-1'; // 设置AWS区域作为项目ID
        selectedProject.value = 'ap-northeast-1'; // 设置selectedProject为AWS区域
        resources.vms = [];
        resources.disks = [];
        resources.zones = [];
        if (autoLoadOnStart) {
          await selectProject(currentProjectId.value);
        }
      } else {
        // GCP模式：获取项目列表
        console.log('GCP模式：开始获取项目...');
        await fetchProjects();
        console.log('项目列表:', projects.value);

        if (projects.value && projects.value.length > 0) {
          console.log('选择第一个项目:', projects.value[0]);
          selectedProject.value = projects.value[0];
          currentProjectId.value = projects.value[0];
          resources.vms = [];
          resources.disks = [];
          resources.zones = [];
          if (autoLoadOnStart) {
            await selectProject(projects.value[0]);
          }
        } else {
          console.warn('没有可用的项目');
        }
      }
    } else {
      console.log('用户未认证，检查认证方式...');
      if (currentProvider.value === 'aws') {
        console.log('AWS模式：检查用户会话...');
        console.log('当前 isAuthenticated:', isAuthenticated.value);
        console.log('当前 user.value:', user.value);

        // 先尝试获取当前用户，避免重复重定向
        try {
          const currentUser = await userManager.value.getUser();
          console.log('userManager.getUser() 结果:', currentUser);
          if (currentUser) {
            console.log('发现现有用户会话:', currentUser);
            cognitoUser.value = currentUser;

            // 从 currentUser 中提取用户信息
            const userInfo = {
              email: currentUser.profile?.email || currentUser.profile?.sub || 'unknown',
              role: 'admin', // 默认角色，可以根据需要调整
              id: currentUser.profile?.sub || currentUser.profile?.id || 'unknown'
            };

            setUser(userInfo);
            console.log('用户会话恢复成功，跳过重定向');
            return;
          }
        } catch (error) {
          console.log('没有现有用户会话，准备重定向到登录页面');
          console.log('userManager.getUser() 错误:', error);
        }

        console.log('AWS模式：准备重定向到登录页面');
        console.log('即将调用 userManager.signinRedirect()');
        await userManager.value.signinRedirect();
      }
    }
  } catch (error) {
    console.error('初始化失败:', error);
  }
});

const onProjectChange = async () => {
  if (selectedProject.value) {
    await selectProject(selectedProject.value);
  }
};

// 统一的资源加载函数
const loadResourcesHandler = async () => {
  if (currentProvider.value === 'aws') {
    // AWS模式：使用统一的API端点
    await selectProject(currentProjectId.value);
  } else {
    // GCP模式，使用项目选择逻辑
    if (selectedProject.value) {
      await selectProject(selectedProject.value);
    } else if (projects.value && projects.value.length > 0) {
      selectedProject.value = projects.value[0];
      await selectProject(projects.value[0]);
    }
  }
};


const filteredResources = computed(() => {
  console.log('App: computing filteredResources, resources:', resources);

  if (!resources) {
    console.log('App: no resources, returning empty');
    return { vms: [], disks: [] };
  }

  const lowerCaseQuery = searchQuery.value.toLowerCase();

  const filteredVMs = resources.vms.filter(vm => {
    const nameMatch = vm.name.toLowerCase().includes(lowerCaseQuery);
    const diskMatch = vm.disks && vm.disks.some(disk => disk.deviceName.toLowerCase().includes(lowerCaseQuery));
    const runningMatch = !showRunningOnly.value || vm.status === 'RUNNING' || vm.status === 'running';
    return (nameMatch || diskMatch) && runningMatch;
  });

  const filteredDisks = resources.disks.filter(disk => {
    const nameMatch = disk.name.toLowerCase().includes(lowerCaseQuery);
    // If a VM is filtered out, we should also filter out its attached disks from the unattached list
    const isAttachedToFilteredVM = filteredVMs.some(vm => vm.disks && vm.disks.some(d => d.source === disk.selfLink));
    return nameMatch && (!disk.users || disk.users.length === 0 || isAttachedToFilteredVM);
  });

  const result = {
    vms: filteredVMs,
    disks: filteredDisks
  };

  console.log('App: filteredResources result:', result);
  return result;
});

// 事件处理方法
function onResourceClick(resource, type) {
  // ResourceDetailsModal已删除，此功能暂时禁用
  console.log('Resource details view disabled - resource:', resource, 'type:', type);
}

function onVMCreated() {
  showCreateVMModal.value = false;
  // 刷新资源列表
  if (selectedProject.value) {
    selectProject(selectedProject.value);
  }
}

function onDiskCreated() {
  showCreateDiskModal.value = false;
  // 刷新资源列表
  if (selectedProject.value) {
    selectProject(selectedProject.value);
  }
}

async function onAttachDisk(diskName, vmName, zone) {
  isOperating.value = true;
  try {
    await attachDiskToVM(diskName, vmName, zone);
    alert('磁盘挂载操作已提交');
  } catch (e) {
    alert(`挂载磁盘失败：${e.message}`);
  } finally {
    isOperating.value = false;
  }
}

async function onVmOperation(action, vmName, zone) {
  isOperating.value = true;
  try {
    await operateVM(action, vmName, zone);
    alert('虚拟机操作已提交');
  } catch (e) {
    alert(`虚拟机操作失败：${e.message}`);
  } finally {
    isOperating.value = false;
  }
}

async function onDetachDisk(diskName, vmName, zone, device = null) {
  isOperating.value = true;
  try {
    await detachDiskFromVM(diskName, vmName, zone, device);
  } catch (e) {
    alert(`分离磁盘失败：${e.message}`);
  } finally {
    isOperating.value = false;
  }
}

async function onDeleteDisk(diskName, zone) {
  isOperating.value = true;
  try {
    await deleteDisk(diskName, zone);
  } catch (e) {
    alert(`删除磁盘失败：${e.message}`);
  } finally {
    isOperating.value = false;
  }
}

async function onLabelsUpdated() {
  // 刷新资源列表以获取最新的标签信息
  if (selectedProject.value) {
    await selectProject(selectedProject.value);
  }
}
</script>

<style scoped>
#app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f7fa;
}

.app-header {
  height: 56px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 2rem;
  box-shadow: 0 2px 8px rgba(80,80,80,0.04);
  z-index: 10;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-weight: 500;
  color: #333;
}


.main-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

.sidebar-gradient {
  width: 320px;
  background: linear-gradient(160deg, #6a82fb 0%, #fc5c7d 100%);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  box-shadow: 2px 0 12px rgba(80,80,80,0.04);
  z-index: 2;
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1.5rem;
}

.logo-icon {
  font-size: 2rem;
  color: #fff;
}

.logo-title {
  font-size: 1.3rem;
  font-weight: bold;
  letter-spacing: 2px;
  color: #fff;
}

.project-selector, .region-info {
  margin-bottom: 1rem;
  color: #fff;
}

.project-selector label, .region-info label {
  font-weight: 500;
  margin-bottom: 0.2rem;
  display: block;
}

.project-selector select {
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  width: 100%;
  margin-top: 0.2rem;
}

.fixed-region {
  color: #28a745 !important;
  font-weight: 600;
  background: rgba(40, 167, 69, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid rgba(40, 167, 69, 0.3);
  display: inline-block;
  margin-top: 0.2rem;
}

.load-prompt {
  margin-bottom: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid rgba(255, 255, 255, 0.3);
}

.load-prompt i {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
}

.load-prompt span {
  font-size: 0.9rem;
  font-weight: 500;
}

.refresh-btn {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  margin-bottom: 1.2rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background 0.2s;
}

.refresh-btn:hover {
  background: rgba(255,255,255,0.25);
}

.stats-cards {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  flex: 1;
  background: rgba(255,255,255,0.18);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(80,80,80,0.07);
  padding: 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
}

.stat-card i {
  font-size: 1.5rem;
  margin-bottom: 0.3rem;
}

.stat-num {
  font-size: 2rem;
  font-weight: bold;
}

.stat-label {
  font-size: 1rem;
  opacity: 0.9;
}

.main-btn {
  width: 100%;
  padding: 1rem 0;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.7rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  box-shadow: 0 2px 8px rgba(80,80,80,0.07);
  transition: background 0.2s, box-shadow 0.2s;
}

.main-btn.green {
  background: linear-gradient(90deg, #43e97b 0%, #38f9d7 100%);
  color: #fff;
}

.main-btn.green:hover {
  background: linear-gradient(90deg, #38f9d7 0%, #43e97b 100%);
}

.main-btn.blue {
  background: linear-gradient(90deg, #396afc 0%, #2948ff 100%);
  color: #fff;
}

.main-btn.blue:hover {
  background: linear-gradient(90deg, #2948ff 0%, #396afc 100%);
}

.navigation {
  margin: 1.2rem 0 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nav-btn {
  background: rgba(255,255,255,0.13);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 0.5rem;
  font-size: 1.05rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: background 0.2s, color 0.2s;
}

.nav-btn.active, .nav-btn:hover {
  background: #fff;
  color: #6a82fb;
}

.filters {
  margin-top: 1.2rem;
}

.search-input {
  width: 100%;
  padding: 0.6rem;
  border-radius: 6px;
  border: none;
  margin-bottom: 0.7rem;
  font-size: 1rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.97rem;
  color: #fff;
}

.content-area {
  flex: 1;
  background: #f4f7fa;
  min-width: 0;
  overflow: auto;
}

.main-title {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 2rem;
  letter-spacing: 1px;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.floating-language-switcher {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.floating-site-switcher {
  position: fixed;
  top: 20px;
  right: 200px;
  z-index: 1000;
}

.sidebar-userinfo {
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255,255,255,0.18);
  color: #fff;
  font-size: 1rem;
  opacity: 0.95;
}
.userinfo-email {
  font-size: 1.05rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
  word-break: break-all;
}
.userinfo-role {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.97rem;
}
</style>
